import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  Chip,
  CircularProgress,
  Alert,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Add as AddIcon,
  Science as LabIcon,
  Image as ImageIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Remove as RemoveIcon
} from '@mui/icons-material';
import { api } from '@services/authService';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { formatDateOnly } from '@utils/dateFormatter';

const DoctorLab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [labResults, setLabResults] = useState<any[]>([]);
  const [imagingStudies, setImagingStudies] = useState<any[]>([]);
  const [tab, setTab] = useState(0);
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [orderType, setOrderType] = useState<'lab' | 'imaging'>('lab');
  const [selectedPatientId, setSelectedPatientId] = useState('');
  const [selectedResult, setSelectedResult] = useState<any>(null);
  const [labTests, setLabTests] = useState<string[]>(['']);

  useEffect(() => {
    loadLabResults();
    loadImagingStudies();
  }, []);

  const loadLabResults = async () => {
    try {
      setLoading(true);
      const response = await api.get('/doctor/lab/results?from=2024-01-01');
      setLabResults(response.data.data);
    } catch (error) {
      console.error('Eroare la incarcarea rezultatelor lab:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadImagingStudies = async () => {
    try {
      const response = await api.get('/doctor/imaging?status=completed');
      setImagingStudies(response.data.data || []);
    } catch (error) {
      console.error('Eroare la incarcarea studiilor imagistice:', error);
    }
  };

  const handleOrderLab = async () => {
    if (!selectedPatientId || labTests.filter(t => t.trim()).length === 0) {
      alert('Completeaza toate campurile');
      return;
    }

    try {
      await api.post('/doctor/lab/orders', {
        patient_id: selectedPatientId,
        tests_ordered: labTests.filter(t => t.trim()),
        priority: 'routine'
      });

      alert('Comanda laborator trimisa cu succes!');
      setOrderDialogOpen(false);
      loadLabResults();
    } catch (error) {
      console.error('Eroare la comanda lab:', error);
      alert('Eroare la trimiterea comenzii');
    }
  };

  const handleOrderImaging = async (modality: string) => {
    if (!selectedPatientId) {
      alert('Selecteaza un pacient');
      return;
    }

    try {
      await api.post('/doctor/imaging', {
        patient_id: selectedPatientId,
        modality
      });

      alert('Comanda imagistica trimisa cu succes!');
      setOrderDialogOpen(false);
      loadImagingStudies();
    } catch (error) {
      console.error('Eroare la comanda imagistica:', error);
    }
  };

  const getFlagColor = (flagged: string) => {
    switch (flagged) {
      case 'C': return 'error';
      case 'H': return 'warning';
      case 'L': return 'info';
      default: return 'success';
    }
  };

  const getFlagLabel = (flagged: string) => {
    switch (flagged) {
      case 'C': return 'CRITIC';
      case 'H': return 'Ridicat';
      case 'L': return 'Scazut';
      case 'N': return 'Normal';
      default: return 'N/A';
    }
  };

  // Tab Lab Results
  const renderLabResults = () => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      );
    }

    if (labResults.length === 0) {
      return <Alert severity="info">Nu exista rezultate laborator</Alert>;
    }

    return (
      <Stack spacing={2}>
        {labResults.map((order: any) => (
          <Card key={order.order_id}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Box>
                  <Typography variant="h6">
                    {order.patient?.name || 'Pacient necunoscut'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Comandat: {formatDateOnly(order.ordered_at)} •
                    Receptionat: {order.received_at ? formatDateOnly(order.received_at) : 'In asteptare'}
                  </Typography>
                </Box>
                <Chip label={order.status} color="primary" />
              </Box>

              {order.results && order.results.length > 0 && (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Test</TableCell>
                        <TableCell>Valoare</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {order.results.map((result: any) => (
                        <TableRow key={result.id}>
                          <TableCell>{result.name}</TableCell>
                          <TableCell>{result.value}</TableCell>
                          <TableCell>
                            <Chip
                              label={getFlagLabel(result.flagged)}
                              color={getFlagColor(result.flagged)}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        ))}
      </Stack>
    );
  };

  // Tab Imaging
  const renderImaging = () => {
    if (imagingStudies.length === 0) {
      return <Alert severity="info">Nu exista studii imagistice</Alert>;
    }

    return (
      <Stack spacing={2}>
        {imagingStudies.map((study: any) => (
          <Card key={study.id}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Box display="flex" alignItems="center" gap={2}>
                  <ImageIcon color="primary" />
                  <Box>
                    <Typography variant="h6">
                      {study.modality} - {study.patient?.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {study.body_part || 'N/A'} • {formatDateOnly(study.created_at)}
                    </Typography>
                  </Box>
                </Box>
                <Chip label={study.status} color="primary" />
              </Box>

              {study.report_text && (
                <Box mt={2}>
                  <Typography variant="subtitle2" gutterBottom>
                    Raport:
                  </Typography>
                  <Typography variant="body2">{study.report_text}</Typography>
                </Box>
              )}

              {study.external_link && (
                <Box mt={2}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => window.open(study.external_link, '_blank')}
                  >
                    Deschide DICOM Viewer
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        ))}
      </Stack>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Lab & Imaging</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOrderDialogOpen(true)}
        >
          Comanda Noua
        </Button>
      </Box>

      {/* Tabs */}
      <Box sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(e, newValue) => setTab(newValue)}>
          <Tab label="Rezultate Laborator" />
          <Tab label="Imagistica" />
        </Tabs>
      </Box>

      {/* Content */}
      {tab === 0 ? renderLabResults() : renderImaging()}

      {/* Order Dialog */}
      <Dialog open={orderDialogOpen} onClose={() => setOrderDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Comanda Noua</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Tip Comanda</InputLabel>
              <Select
                value={orderType}
                onChange={(e) => setOrderType(e.target.value as any)}
                label="Tip Comanda"
              >
                <MenuItem value="lab">Laborator</MenuItem>
                <MenuItem value="imaging">Imagistica</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="ID Pacient"
              value={selectedPatientId}
              onChange={(e) => setSelectedPatientId(e.target.value)}
              helperText="In productie: autocomplete cu cautare"
            />

            {orderType === 'lab' ? (
              <>
                <Typography variant="subtitle2">Analize Solicitate:</Typography>
                {labTests.map((test, index) => (
                  <Box key={index} display="flex" gap={1}>
                    <TextField
                      fullWidth
                      size="small"
                      label={`Analiza ${index + 1}`}
                      value={test}
                      onChange={(e) => {
                        const updated = [...labTests];
                        updated[index] = e.target.value;
                        setLabTests(updated);
                      }}
                      placeholder="Ex: Hemoleucograma completa"
                    />
                    {labTests.length > 1 && (
                      <IconButton
                        size="small"
                        onClick={() => setLabTests(labTests.filter((_, i) => i !== index))}
                      >
                        <RemoveIcon />
                      </IconButton>
                    )}
                  </Box>
                ))}
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setLabTests([...labTests, ''])}
                >
                  Adauga Analiza
                </Button>
              </>
            ) : (
              <FormControl fullWidth>
                <InputLabel>Modalitate</InputLabel>
                <Select label="Modalitate">
                  <MenuItem value="X-Ray" onClick={() => handleOrderImaging('X-Ray')}>
                    Radiografie (X-Ray)
                  </MenuItem>
                  <MenuItem value="Ultrasound" onClick={() => handleOrderImaging('Ultrasound')}>
                    Ecografie (Ultrasound)
                  </MenuItem>
                  <MenuItem value="CT" onClick={() => handleOrderImaging('CT')}>
                    Computer Tomografie (CT)
                  </MenuItem>
                  <MenuItem value="MRI" onClick={() => handleOrderImaging('MRI')}>
                    Rezonanta Magnetica (MRI)
                  </MenuItem>
                </Select>
              </FormControl>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOrderDialogOpen(false)}>Anuleaza</Button>
          <Button variant="contained" onClick={orderType === 'lab' ? handleOrderLab : () => {}}>
            Trimite Comanda
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DoctorLab;

